import React from "react";
import {
  faCircleInfo,
  faXmark,
  faListCheck,
  faProjectDiagram,
  faInfoCircle,
  faFolderOpen,
  faClock,
  faMoneyBill,
  faChartLine,
  faTasks,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const statusGroups = [
  {
    title: "Planning",
    icon: faListCheck,
    statuses: [
      { value: "DRAFT", label: "Draft", description: "Initial state; task is created but not finalized." },
      { value: "PLANNED", label: "Planned", description: "Task is reviewed and scheduled for work." },
    ],
  },
  {
    title: "Execution",
    icon: faProjectDiagram,
    statuses: [
      { value: "ASSIGNED", label: "Assigned", description: "Task is assigned to a responsible person/team." },
      { value: "INITIATED", label: "Initiated", description: "Assigned resource has acknowledged and started setup." },
      { value: "IN_PROGRESS", label: "In Progress", description: "Active work is underway on the task." },
      { value: "ON_HOLD", label: "On Hold", description: "Task is paused due to pending issues or dependencies." },
      { value: "REOPENED", label: "Reopened", description: "Previously completed task is reopened due to issues." },
    ],
  },
  {
    title: "Review & Completion",
    icon: faInfoCircle,
    statuses: [
      { value: "UNDER_REVIEW", label: "Under Review", description: "Submitted for quality/stakeholder review." },
      { value: "COMPLETED", label: "Completed", description: "Task execution is complete." },
      { value: "APPROVED", label: "Approved", description: "Final validation is done and task is accepted." },
      { value: "REJECTED", label: "Rejected", description: "Work was not accepted during review and must be reworked." },
    ],
  },
  {
    title: "Closure",
    icon: faXmark,
    statuses: [
      { value: "TERMINATED", label: "Terminated", description: "Forcefully closed before completion due to valid reason." },
      { value: "CANCELLED", label: "Cancelled", description: "Planned task is no longer required and is canceled." },
    ],
  },
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const TaskStatusHelpModal: React.FC<Props> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-4xl overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faCircleInfo} /> Project Task Module Guide
          </h2>
          <button onClick={onClose} className="text-gray-600 hover:text-red-500">
            <FontAwesomeIcon icon={faXmark} size="lg" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Understand the lifecycle, statuses, and key fields used in managing project tasks effectively.
        </p>

        {/* Status Groups */}
        {statusGroups.map((group) => (
          <div key={group.title} className="mb-6">
            <h3 className="text-lg font-semibold text-indigo-700 mb-2 flex items-center gap-2">
              <FontAwesomeIcon icon={group.icon} />
              {group.title}
            </h3>
            <ul className="space-y-2 pl-5 border-l-4 border-indigo-300">
              {group.statuses.map((status) => (
                <li key={status.value} className="text-sm text-gray-700">
                  <strong>{status.label}</strong>: {status.description}
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Task Fields Overview */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-indigo-800 flex items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faFolderOpen} />
            Key Task Fields
          </h3>
          <ul className="list-disc ml-6 space-y-2 text-sm text-gray-700">
            <li><strong>Title & Description:</strong> Human-readable identification of the task.</li>
            <li><strong>Estimated vs. Actual:</strong> Quantity, rate, effort, and amount tracked against initial plan.</li>
            <li><strong>Variance Calculations:</strong> Automatic fields to identify cost/effort overruns.</li>
            <li><strong>Status:</strong> Indicates current phase of the task lifecycle.</li>
            <li><strong>Dates:</strong> Includes start/end (planned and actual) to monitor schedules.</li>
            <li><strong>Assigned To / Completed By / Approved By:</strong> Tracks responsibilities and accountability.</li>
            <li><strong>Progress (%):</strong> Manually or automatically updated to reflect how far along the task is.</li>
          </ul>
        </div>

        {/* Metrics and KPIs */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-indigo-800 flex items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faChartLine} />
            Task Metrics & KPIs
          </h3>
          <ul className="list-disc ml-6 space-y-2 text-sm text-gray-700">
            <li><strong>Completion %:</strong> (Completed / Total Tasks) to show project progress.</li>
            <li><strong>Effort & Cost Overruns:</strong> Calculated using generated columns based on actual vs estimated.</li>
            <li><strong>Delayed Tasks:</strong> Where actual_end_date exceeds given_end_date.</li>
            <li><strong>Billable vs. Non-Billable:</strong> Determines invoicing logic for tasks.</li>
          </ul>
        </div>

        {/* Notes */}
        <div className="mt-6 border-t pt-4 text-xs text-gray-500">
          <p><strong>Note:</strong> Fields such as cost variance and effort variance are computed and cannot be manually edited. Ensure proper planning to avoid overruns.</p>
        </div>
      </div>
    </div>
  );
};

export default TaskStatusHelpModal;